import 'leaflet/dist/leaflet.css';

import { type IAddress } from '@mahawi/eshop-common/src/types';
import { Alert, Drawer, Grid, Skeleton } from 'antd';
import React from 'react';
import { CircleMarker, MapContainer, TileLayer } from 'react-leaflet';

import { EGeocodeError, useGeocode } from '../../hooks/use-geocode';
import { COLOR } from '../../theme/tokens';

const geocodeErrorMessages: Record<EGeocodeError, string> = {
  [EGeocodeError.NOT_FOUND]:
    'Address could not be found on the map. Check the street, city and zip.',
  [EGeocodeError.UNAVAILABLE]:
    'Map service is unavailable. Check that the Nominatim service is running.',
};

interface IAddressMapDrawerProps {
  address: IAddress;
  open: boolean;
  onClose: () => void;
}

export function AddressMapDrawer({
  address,
  open,
  onClose,
}: IAddressMapDrawerProps): React.ReactElement {
  const { lat, lon, loading, error } = useGeocode(address);
  const { md: isDesktop } = Grid.useBreakpoint();

  return (
    <Drawer
      open={open}
      onClose={onClose}
      styles={{
        wrapper: { width: isDesktop ? '40%' : '90%' },
        body: { padding: 0 },
      }}
      title="Address map"
    >
      {loading && <Skeleton active />}
      {!loading && error && (
        <Alert type="error" title={geocodeErrorMessages[error]} />
      )}
      {!loading && !error && lat !== null && lon !== null && (
        <MapContainer
          center={[lat, lon]}
          zoom={7}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url="/api/map/tiles/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          />
          <CircleMarker center={[lat, lon]} radius={10} color={COLOR.error} />
        </MapContainer>
      )}
    </Drawer>
  );
}
